/* eslint-disable i18next/no-literal-string */
import { PageIndicator } from '@lotta/self-service/lottery-fe/ui-common';
import { FormPage } from './FormPage';
import { toPayload } from './modules/transform';
import { ReviewConfiguration } from './review-configuration/ReviewConfiguration';
import type { LotteryConfigData, LotteryFormAPIErrorType, LotteryFormTemplate } from './types';
import { useFormWizard } from './utils/use-form-wizard';

// eslint-disable-next-line i18next/no-literal-string
const MODULE_PAGES: ReadonlyArray<keyof LotteryFormTemplate> = [
  'general',
  'legal',
  'signup',
  'selectWinners',
  'winnerCommunication',
] as const;

const REVIEW_PAGE = 'review';

// All the form wizard pages
const PAGES = [...MODULE_PAGES, REVIEW_PAGE] as const;

// Total pages for page-indicator, add 2 to include the 'select-template'-page and 'pre-config'-page
const PAGES_COUNT = PAGES.length + 2;

export type LotteryFormProps = {
  templateId: string;
  template: LotteryFormTemplate;
  retailUnit: string;
  values?: Record<string, unknown>; // When used for editing a lottery
  onFormSubmit: (lotteryConfigData: LotteryConfigData) => void;
  onSecondaryAction: () => void;
  error?: LotteryFormAPIErrorType;
};

/**
 * Create a form for create or edit lottery based on modules
 */
export const LotteryForm: React.FC<LotteryFormProps> = ({
  templateId,
  template,
  retailUnit,
  values: initialValues,
  onFormSubmit,
  onSecondaryAction,
  error,
}) => {
  const { getFormState, currentPage } = useFormWizard(PAGES, onSecondaryAction, onFormSubmit);

  if (template === undefined) {
    return null; // an error
  }

  if (currentPage.id === 'review') {
    const lotteryConfigData = toPayload(templateId, getFormState());

    return (
      <ReviewConfiguration
        lotteryConfigData={lotteryConfigData}
        onSecondaryAction={currentPage.onSecondaryAction}
        onFormSubmit={currentPage.onFormSubmit}
        error={error}
        wrapFormTitle={(title: string) => (
          <PageIndicator pages={PAGES_COUNT} currentPage={PAGES_COUNT} text={title} />
        )}
      />
    );
  }

  return (
    <FormPage
      key={currentPage.id}
      templateId={templateId}
      retailUnit={retailUnit}
      modules={template[currentPage.id]}
      values={currentPage.values}
      initialValues={initialValues}
      onSecondaryAction={currentPage.onSecondaryAction}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFormSubmit={currentPage.onFormSubmit as any}
      submitTranslationKey="general-next"
      secondaryActionTranslationKey="general-back"
      wrapFormTitle={(title: string) => (
        <PageIndicator pages={PAGES_COUNT} currentPage={currentPage.index + 3} text={title} />
      )}
      getFormState={getFormState}
    />
  );
};
